import { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  GridProps,
  Stack,
  StackProps,
  Typography,
} from '@mui/material';
import { size } from 'lodash';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

interface CheckboxFilterProps {
  options: {
    label: string;
    value: string | number;
    svg?: {
      active: React.ReactNode;
      default: React.ReactNode;
    };
  }[];
  value?: Array<string | number> | number | string;
  onChange: (value?: Array<string | number> | number | string) => void;
  withIcon?: boolean;
  displayButton?: boolean;
  GridProps?: GridProps;
  containerProps?: StackProps;
}

const CheckboxFilter: React.FC<CheckboxFilterProps> = ({
  onChange,
  value,
  options,
  withIcon,
  displayButton,
  GridProps,
  containerProps,
}) => {
  const allSelected = options?.map((item) => item.value);
  const [selected, setSelected] = useState<
    Array<string | number> | number | string | undefined
  >(value ?? allSelected);

  const selectedAll = isArray(selected) && size(selected) === size(options);

  const handleCheckboxChange = (value: string | number) => {
    if (isArray(selected) && selected?.includes(value)) {
      const changedValue = selected?.filter(
        (item: string | number) => item !== value
      );
      setSelected(changedValue);
      !displayButton && onChange(changedValue);
    } else {
      const changedValue = isArray(selected) ? [...selected, value] : [value];
      setSelected(changedValue);
      if (isArray(changedValue) && size(changedValue) === size(options)) {
        !displayButton && onChange([]);
        return;
      }
      !displayButton && onChange(changedValue);
    }
  };
  const onClickButton = () => {
    onChange(isEmpty(selected) || selectedAll ? [] : selected);
  };

  const onReset = () => {
    setSelected(allSelected);
    onChange([]);
  };

  const checkboxSx = {
    '&.Mui-checked': {
      color: '#31ACBF',
    },
    color: '#A8B5C9',
    transform: 'scale(0.889)',
    width: '16px !important',
    height: 16,
    marginRight: '6.5px',
  };

  const isIncluded = (value: string | number) => {
    if (isArray(selected)) {
      return selected?.includes(value);
    } else {
      return selected === value;
    }
  };

  return (
    <>
      <Stack paddingBottom={15} {...containerProps}>
        <Grid container direction="row" spacing={2.5}>
          <Grid item xs={6} {...GridProps}>
            <FormControlLabel
              onClick={() => {
                if (selectedAll) {
                  setSelected([]);
                  !displayButton && onChange([]);
                  return;
                }
                setSelected(allSelected);
                !displayButton && onChange([]);
              }}
              sx={{ ml: 0 }}
              control={
                <Checkbox size="medium" checked={selectedAll} sx={checkboxSx} />
              }
              label={
                <Typography
                  fontSize={14}
                  sx={{
                    mt: 0.5,
                    lineHeight: '20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    ml: withIcon ? 2.5 : 0,
                  }}
                >
                  All
                </Typography>
              }
            />
          </Grid>
          {options.map((item, idx) => (
            <Grid item xs={6} {...GridProps} key={idx}>
              <FormControlLabel
                onChange={() => handleCheckboxChange(item.value)}
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="medium"
                    checked={isIncluded(item.value)}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" gap={1.25}>
                    {withIcon &&
                      (isIncluded(item.value)
                        ? item.svg?.active
                        : item.svg?.default)}
                    <Typography
                      fontSize={14}
                      sx={{
                        mt: 0.2,
                        textOverflow: 'ellipsis',
                        lineHeight: '20px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
      {displayButton && (
        <Stack
          width={'100%'}
          direction={'row'}
          alignItems={'center'}
          position={'absolute'}
          sx={{ background: 'white', bottom: 0 }}
        >
          <Button
            fullWidth
            onClick={onReset}
            variant={'outlined'}
            color="secondary"
            sx={{
              padding: '7px 22px',
              textTransform: 'capitalize',
              borderRadius: 0,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            Reset
          </Button>
          <Button
            onClick={onClickButton}
            fullWidth
            variant="contained"
            color="secondary"
            sx={{
              padding: '7px 22px',
              textTransform: 'capitalize',
              borderRadius: 0,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            Apply
          </Button>
        </Stack>
      )}
    </>
  );
};

export default CheckboxFilter;
